import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {lerpGreenToRedViaOrange} from "../../helpers/scaling";
import {scaleNumber} from "../../lq_js_common/helpers/scaling";

export class ShaperCircuitAsnSankey extends BaseDashlet {
    constructor(slot, direction = "download") {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.direction = direction;
    }

    title() { return this.direction === 'upload' ? "Circuit ASN Sankey (Upload)" : "Circuit ASN Sankey (Download)"; }
    supportsZoom() { return true; }
    tooltip() { return "<h5>Top ASN Sankey</h5><p>Top ASNs for this circuit." + (this.direction === 'upload' ? " (Upload)" : " (Download)") + "</p>"; }

    buildContainer() { let base = super.buildContainer(); base.appendChild(this.graphDiv()); return base; }
    setup() { super.setup(); this.graph = new CircuitAsnSankeyGraph(this.graphDivId(), this.direction); if (this.lastGraphState) this.graph.update(this.lastGraphState); }
    channelRequests() { return ["CircuitAsn"]; }

    onMessage(msg) {
        if (!msg.Data || msg.Data.target !== "circuit_asn") return;
        const rows = msg.Data.blob.rows || [];
        // pick top 5 by chosen direction
        const sorted = [...rows].sort((a,b) => {
            const av = this.direction === 'upload' ? Number(a.bytes_up) : Number(a.bytes_down);
            const bv = this.direction === 'upload' ? Number(b.bytes_up) : Number(b.bytes_down);
            return bv - av;
        }).slice(0, 5);
        const data = sorted.map(r => ({
            name: r.name,
            value: this.direction === 'upload' ? Number(r.bytes_up) : Number(r.bytes_down),
            retransmit_percent: this.direction === 'upload' ? (r.retransmit_up_percent ?? 0) : (r.retransmit_down_percent ?? 0)
        }));

        const myDiv = document.getElementById(this.id);
        if (!this.rendered) { myDiv.classList.add("card", "animate__animated", "animate__fadeIn"); }
        else {
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => myDiv.classList.remove("animate__animated", "animate__pulse"), 1000);
        }
        this.graph.update(data);
        this.lastGraphState = data;
        this.rendered = true;
    }
}

class CircuitAsnSankeyGraph extends DashboardGraph {
    constructor(id, direction) {
        super(id);
        this.direction = direction;
        this.currentData = [];
        this.option = {
            tooltip: {
                trigger: 'item',
                triggerOn: 'mousemove',
                formatter: function(params) {
                    if (params.dataType === 'edge') {
                        const d = params.data;
                        const formattedBytes = scaleNumber(d.value, 2);
                        // For upload, use the target (ASN) node for retransmit; for download, use the source (ASN)
                        const endpointName = (this.direction === 'upload') ? d.target : d.source;
                        const endpointNode = this.currentData.find(x => x.name === endpointName);
                        const rx = endpointNode ? Number(endpointNode.retransmit_percent).toFixed(2) : 'N/A';
                        const dir = this.direction === 'upload' ? 'Upload' : 'Download';
                        return `${d.source} → ${d.target}<br/>${dir} Bytes: ${formattedBytes}<br/>TCP Retransmit: ${rx}%`;
                    } else if (params.dataType === 'node') {
                        if (params.name === 'Circuit') return 'Circuit';
                        const nodeData = this.currentData.find(d => d.name === params.name);
                        if (nodeData) {
                            const formattedBytes = scaleNumber(nodeData.value, 2);
                            const rx = Number(nodeData.retransmit_percent).toFixed(2);
                            const dir = this.direction === 'upload' ? 'Upload' : 'Download';
                            return `<strong>${params.name}</strong><br/>${dir} Bytes: ${formattedBytes}<br/>TCP Retransmit: ${rx}%`;
                        }
                        return params.name;
                    }
                    return '';
                }.bind(this)
            },
            series: [
                { type: 'sankey', data: [], links: [], emphasis: { focus: 'adjacency' }, lineStyle: { color: 'gradient', curveness: 0.5 }, orient: 'horizontal', layoutIterations: 0 }
            ]
        };
        this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();
        this.currentData = data;

        let nodes = [];
        let links = [];

        if (this.direction === 'upload') {
            // Mirror: Circuit on the left, ASNs on the right, Circuit → ASN
            nodes.push({ name: 'Circuit', x: 100, y: 300, itemStyle: { color: '#9b59b6' }, label: { show: false } });
            data.forEach((item, index) => {
                const color = lerpGreenToRedViaOrange(Math.min(10, Math.max(0, 10 - item.retransmit_percent)), 10.0);
                nodes.push({ name: item.name, x: 500, y: 50 + (index * 60), itemStyle: { color } });
                links.push({ source: 'Circuit', target: item.name, value: item.value });
            });
            this.option.series[0] = {
                ...this.option.series[0], data: nodes, links,
                left: '5%', right: '30%', top: '5%', bottom: '5%', nodeWidth: 20, nodeGap: 10, layoutIterations: 0
            };
        } else {
            // Download: ASNs on the left → Circuit on the right
            nodes.push({ name: 'Circuit', x: 700, y: 300, itemStyle: { color: '#9b59b6' }, label: { show: false } });
            data.forEach((item, index) => {
                const color = lerpGreenToRedViaOrange(Math.min(10, Math.max(0, 10 - item.retransmit_percent)), 10.0);
                nodes.push({ name: item.name, x: 100, y: 50 + (index * 60), itemStyle: { color } });
                links.push({ source: item.name, target: 'Circuit', value: item.value });
            });
            this.option.series[0] = {
                ...this.option.series[0], data: nodes, links,
                left: '5%', right: '5%', top: '5%', bottom: '5%', nodeWidth: 20, nodeGap: 10, layoutIterations: 0
            };
        }
        this.chart.setOption(this.option);
    }
}
